import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import '../EmailList.css';
import { apiFetch } from '../utils/api';

// Status configuration with icons, styling and explanations
const getStatusConfig = (status, errorMessage) => {
  if (status === 'success') {
    return {
      label: 'Procesat',
      icon: '✓',
      class: 'success',
      explanation: 'Factură generată cu succes din acest email'
    };
  }
  if (status === 'ignored') {
    return {
      label: 'Ignorat',
      icon: '⊘',
      class: 'ignored',
      explanation: 'Expeditor neautorizat — emailul nu a fost procesat'
    };
  }
  if (status === 'duplicate') {
    return {
      label: 'Duplicat',
      icon: '⧉',
      class: 'duplicate',
      explanation: errorMessage
        || 'Fișier atașat identic cu unul deja facturat — nicio factură nouă creată'
    };
  }
  // All other statuses (pending, processing, failed, skipped, partial_success) = Neprocesat
  // Show actual error message from backend if available
  const errorExplanation = errorMessage
    ? errorMessage
    : 'În așteptare — emailul urmează să fie procesat';

  return {
    label: 'Neprocesat',
    icon: '!',
    class: 'pending',
    explanation: errorExplanation
  };
};

// Status badge component with icon
const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  return (
    <span className={`status-badge status-badge-${config.class}`}>
      <span className="status-badge-icon">{config.icon}</span>
      <span className="status-badge-label">{config.label}</span>
    </span>
  );
};

// Helper function to format date in Romanian timezone
const formatDate = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString('ro-RO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest'
  });
};

// Loading state
const LoadingState = () => (
  <div className="loading-container">
    <div className="loading-content">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="loading-skeleton">
          <div className="skeleton-box skeleton-email"></div>
        </div>
      ))}
    </div>
  </div>
);

// Main email table component
const EmailTable = ({ emails }) => {
  return (
    <div className="email-table-container">
      {/* Main Grid */}
      <div className="email-grid">
        <div className="email-grid-header">
          <span className="col col-status">Status</span>
          <span className="col col-sender">Expeditor</span>
          <span className="col col-subject">Subiect</span>
          <span className="col col-attachments">Atașamente</span>
          <span className="col col-received">Primit</span>
          <span className="col col-invoice">Factură</span>
        </div>

        {emails.map((email) => {
          // All emails now go to the unified email viewer
          const emailLink = `/email/${email.id}`;
          const statusConfig = getStatusConfig(email.processing_status, email.error_message);

          return (
            <Link
              key={email.id}
              to={emailLink}
              className={`email-row-container email-row-${statusConfig.class}`}
            >
              {/* Main row */}
              <div className="email-grid-row email-grid-row-link">
                <div className="col col-status">
                  <StatusBadge status={email.processing_status} />
                </div>

                <div className="col col-sender">
                  <div className="sender-block">
                    <span className="sender-name">{email.sender_name || 'Necunoscut'}</span>
                    <span className="sender-email">{email.sender_email || 'N/A'}</span>
                  </div>
                </div>

                <div className="col col-subject">
                  <span className="email-subject">{email.subject || '(fără subiect)'}</span>
                </div>

                <div className="col col-attachments">
                  <span className="attachment-count">
                    {email.attachment_count > 0 ? email.attachment_count : '—'}
                  </span>
                </div>

                <div className="col col-received">
                  <span className="date-text">{formatDate(email.received_at)}</span>
                </div>

                <div className="col col-invoice">
                  {email.invoices && email.invoices.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {email.invoices.map((invoice, idx) => (
                        <Link
                          key={idx}
                          to={`/invoices/${invoice.id}`}
                          className="invoice-badge invoice-badge-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {invoice.numar_factura}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className="no-invoice">—</span>
                  )}
                </div>
              </div>

              {/* Explanation row */}
              <div className={`email-explanation-row email-explanation-${statusConfig.class}`}>
                <span className="explanation-icon">{statusConfig.icon}</span>
                <span className="explanation-text">{statusConfig.explanation}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// Custom hook to fetch emails with pagination
const useEmails = () => {
  const [emails, setEmails] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchEmails = async (pageNum, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setStatus('loading');
    }

    try {
      const response = await apiFetch(`/api/emails?page=${pageNum}&page_size=10`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      // Update state with paginated response
      if (append) {
        setEmails(prev => [...prev, ...data.emails]);
      } else {
        setEmails(data.emails);
      }

      setHasNext(data.has_next);
      setTotal(data.total);
      setPage(data.page);
      setStatus('success');
      setLoadingMore(false);
    } catch (err) {
      setError(err.message);
      setStatus('error');
      setLoadingMore(false);
      console.error('Error fetching emails:', err);
    }
  };

  const loadMore = () => {
    if (hasNext && !loadingMore) {
      fetchEmails(page + 1, true);
    }
  };

  useEffect(() => { fetchEmails(1); }, []);

  return { emails, status, error, hasNext, total, loadMore, loadingMore };
};

// Main component
export default function EmailListPage() {
  const { emails, status, error, hasNext, total, loadMore, loadingMore } = useEmails();
  const location = useLocation();
  const backPath = getBackPath(location, '/validation-hub');

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emailuri', to: null }
  ];

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return <LoadingState />;
      case 'error':
        return (
          <div className="error-container">
            <div className="error-icon">
              <div className="error-icon-dot"></div>
            </div>
            <h3 className="error-title">Eroare de Conectare</h3>
            <p className="error-message">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="error-btn"
            >
              Reîncarcă Pagina
            </button>
          </div>
        );
      case 'success':
        return emails.length > 0 ? (
          <>
            <EmailTable emails={emails} />

            {/* Pagination Info & Load More Button */}
            <div className="pagination-container" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              padding: '2rem 0',
              marginTop: '1rem'
            }}>
              {/* Show count info */}
              <div style={{
                fontSize: '0.875rem',
                color: '#746c5a',
                textAlign: 'center'
              }}>
                Se afișează {emails.length} din {total} emailuri
              </div>

              {/* Load More Button */}
              {hasNext && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="load-more-btn"
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: loadingMore ? '#e2ded3' : '#b09244',
                    color: loadingMore ? '#a69e8e' : 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loadingMore ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingMore) {
                      e.target.style.backgroundColor = '#9d8136';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingMore) {
                      e.target.style.backgroundColor = '#b09244';
                    }
                  }}
                >
                  {loadingMore ? 'Se încarcă...' : 'Încarcă mai multe'}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="empty-container">
            <div className="empty-icon">
              <div className="empty-icon-dot"></div>
            </div>
            <h3 className="empty-title">
              Nu există emailuri
            </h3>
            <p className="empty-message">
              Nu au fost găsite emailuri în sistem.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="email-list-page">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Validare"
        title="Lista Emailuri"
        subtitle="Toate emailurile primite și procesate de sistem"
        breadcrumbs={breadcrumbs}
      />

      <div style={{ padding: '0 2rem', marginTop: '0.5rem' }}>
        <Link to="/senders-triage" className="review-btn">
          Expeditori ignorați — triaj →
        </Link>
      </div>

      {/* Main Content */}
      <main className="email-list-main">
        {renderContent()}
      </main>
    </div>
  );
}
